import { dwService } from 'amb/services/dw.service';
import frontendUtils from 'honda_cars_ecux/utils/frontendUtils';
import { tabIdentity } from '../utils/tabIdentity';

/**
 * One opening-hours line from `models/store` `storeHoursRows`.
 *
 * @typedef {Object} HondaCentreHoursRow
 *
 * @property {string} [label]
 * @property {string} [hours]
 */

/**
 * Store-shaped dealer used to paint Honda Centre chrome (`Stores-GetDealer` or Flow B mapped fields).
 *
 * @typedef {Object} HondaCentreDealer
 *
 * @property {string} [name]
 * @property {string} [address]
 * @property {string} [ID]
 * @property {string} [expertNo]
 * @property {string} [postalCode]
 * @property {string} [phone]
 * @property {HondaCentreHoursRow[]} [storeHoursRows]
 */

/**
 * Global "Your selected Honda Centre" component.
 *
 * Renders the user's preferred dealer (server-side from `pdict.configuratorModel.userLocation`)
 * plus a "Change Honda Centre" CTA that delegates to the shared `showroom-locator` modal via
 * `data-view`. Listens for `amb:showroom-locator:showroom-changed` and re-renders every
 * `.selected-honda-centre` block in the DOM from the dispatched dealer dataset
 * (`event.detail.dealername` → `.name`, `event.detail.storeaddress` → `.address`).
 *
 * Isolation on: `Stores-GetDealer` overwrites Show-baked chrome with this tab's store.
 *
 * Companion ISML: templates/default/components/selectedHondaCentre.isml
 * Companion SCSS: client/default/scss/v2/components/_selected-honda-centre.scss
 */
export const selectedHondaCentre = {
  /**
   * Bootstrap the component. Idempotent; safe to call once at boot.
   */
  selectedHondaCentre() {
    this.helpers.bootstrap();
    this.helpers.initTabHydrate();
  },

  /**
   * Controller / state container. Skipped by `processInclude` (not a function).
   */
  helpers: {
    /**
     * Honda Centre chrome wrappers. Child `.name` / `.address` / `.location .summary` are shared; layout stays on the wrapper.
     *
     * @private
     * @readonly
     */
    CHROME_SELECTOR: '.selected-centre, .selected-honda-centre, .availability-honda-centre',

    /**
     * Subscribe to `amb:showroom-locator:showroom-changed`. `refreshAll()` re-queries the DOM
     * on every dispatch so blocks rendered after boot (e.g. modal views) refresh too.
     */
    bootstrap() {
      document.body.addEventListener('amb:showroom-locator:showroom-changed', event => {
        this.refreshAll(event.detail);
      });
    },

    /**
     * Isolation-on only. Isolation off keeps Product-Show StoreMgr bake.
     *
     * @private
     */
    initTabHydrate() {
      if (!tabIdentity.isTabIsolation()) {
        return;
      }

      // Sidebar `updateTabLocation` already triggers `location:afterUpdate` on boot.
      $('body').on('location:afterUpdate', (event, data) => this.onLocationAfterUpdate(event, data));
    },

    /**
     * Ignore `tempuserloc`. Re-fetch store + paint on `userloc`.
     *
     * @private
     *
     * @param {JQuery.Event} event
     * @param {{ cookieID?: string }} [data]
     */
    onLocationAfterUpdate(event, data) {
      if (data && data.cookieID && data.cookieID !== 'userloc') {
        return;
      }

      this.fetchAndPaint();
    },

    /**
     * Paint `.location .summary` from the `userloc` cookie. Missing nodes are skipped.
     *
     * @private
     *
     * @param {{ summary?: string } | null} userLocation
     */
    updateLocationSummary(userLocation) {
      const summary = (userLocation && userLocation.summary) || '';

      for (const container of document.querySelectorAll(this.CHROME_SELECTOR)) {
        const summaryEl = container.querySelector('.location .summary');

        if (summaryEl) {
          summaryEl.textContent = summary;
        }
      }
    },

    /**
     * `Stores-GetDealer` then paint. No-op when this page has no Honda Centre chrome.
     *
     * @private
     */
    fetchAndPaint() {
      if (!document.querySelector(this.CHROME_SELECTOR)) {
        return;
      }

      const userLocation = JSON.parse(frontendUtils.getCookie('userloc') || 'null');

      this.updateLocationSummary(userLocation);

      const dealerId = userLocation && userLocation.dealer && userLocation.dealer.dealerId;

      if (!dealerId) {
        return;
      }

      $.ajax({
        url: dwService.url('Stores-GetDealer'),
        type: 'get',
        dataType: 'json',
        data: { dealerId }
      }).done(response => {
        if (!response || !response.success || !response.dealer) {
          return;
        }

        for (const container of document.querySelectorAll(this.CHROME_SELECTOR)) {
          this.updateDealer(container, response.dealer);
        }

        this.updateAvailabilityCta(response.dealer);
      });
    },

    /**
     * Paint one Honda Centre wrapper. Missing nodes are skipped.
     *
     * @private
     *
     * @param {HTMLElement} container
     * @param {HondaCentreDealer} dealer
     */
    updateDealer(container, dealer) {
      const nameEl = container.querySelector('.name');
      const addressEl = container.querySelector('.address');
      const detailsEl = container.querySelector('.details');
      const postCodeInput = container.querySelector('input[name="AddressPostcode"]');
      const dealerIdInput = container.querySelector('input[name="DealerId"]');
      const expertIdInput = container.querySelector('input[name="ExpertId"]');
      const infoContainer = container.querySelector('.info');

      if (nameEl) {
        nameEl.textContent = dealer.name || '';
      }

      if (addressEl) {
        addressEl.textContent = dealer.address || '';
      }

      if (postCodeInput) {
        postCodeInput.value = dealer.postalCode || '';
      }

      if (dealerIdInput) {
        dealerIdInput.value = dealer.ID || '';
      }

      if (expertIdInput) {
        expertIdInput.value = dealer.expertNo || '';
      }

      detailsEl?.classList.toggle('d-none', !dealer.name);

      if (!infoContainer) {
        return;
      }

      const phoneRow = infoContainer.querySelector('.phone-row');
      const phoneValue = phoneRow?.querySelector('.value');
      const hoursTemplate = container.querySelector('template[data-name="hours-row"]');
      const storeHoursRows = dealer.storeHoursRows || [];

      if (phoneValue) {
        phoneValue.textContent = dealer.phone || '';
      }

      phoneRow?.classList.toggle('d-none', !dealer.phone);

      infoContainer.querySelectorAll('.hours-row').forEach(row => row.remove());

      if (!hoursTemplate) {
        return;
      }

      for (let i = 0; i < storeHoursRows.length; i++) {
        const hoursRow = this.createHoursRow(hoursTemplate, storeHoursRows[i].hours || '', storeHoursRows[i].label || '');

        if (hoursRow) {
          infoContainer.appendChild(hoursRow);
        }
      }
    },

    /**
     * Call Honda Centre CTA (footer, not inside a chrome wrapper).
     *
     * @private
     *
     * @param {HondaCentreDealer} dealer
     */
    updateAvailabilityCta(dealer) {
      const callCta = document.querySelector('.availability-cta');

      if (!callCta) {
        return;
      }

      const hasPhone = Boolean(dealer.phone);

      callCta.setAttribute('href', hasPhone ? 'tel:' + dealer.phone : '#');
      callCta.classList.toggle('disabled', !hasPhone);

      if (hasPhone) {
        callCta.removeAttribute('aria-disabled');
        callCta.removeAttribute('tabindex');

        return;
      }

      callCta.setAttribute('aria-disabled', 'true');
      callCta.setAttribute('tabindex', '-1');
    },

    /**
     * Clone a `<template data-name="hours-row">`.
     *
     * @private
     *
     * @param {HTMLTemplateElement} template
     * @param {string} value
     * @param {string} [label]
     *
     * @returns {HTMLElement|null}
     */
    createHoursRow(template, value, label) {
      const node = template.content.firstElementChild;

      if (!node) {
        return null;
      }

      const row = node.cloneNode(true);
      const labelEl = row.querySelector('.label');
      const valueEl = row.querySelector('.value');

      if (labelEl && label !== undefined) {
        labelEl.textContent = label;
      }

      if (valueEl) {
        valueEl.textContent = value;
      }

      return row;
    },

    /**
     * Re-render every `.selected-honda-centre` block from the dispatched dealer dataset.
     *
     * @private
     *
     * @param {Record<string, string> | null | undefined} detail
     *   Spread of the `:checked` dealer-radio's `dataset` (typically `dealername`,
     *   `storeaddress`, `portid`, `expertid`, `storepostcode`).
     */
    refreshAll(detail) {
      if (!detail || !detail.dealername) {
        return;
      }

      const dealer = {
        name: detail.dealername,
        address: detail.storeaddress,
        postalCode: detail.storepostcode,
        ID: detail.dealerid,
        expertNo: detail.expertid
      };

      for (const block of document.querySelectorAll('.selected-honda-centre:not(.readonly)')) {
        this.updateDealer(block, dealer);
      }
    }
  }
};
